import { isTauriMode } from "../environmentDetector";

export type StoreLocationControl = {
  locationControlEnabled?: boolean;
  locationLatitude?: number | null;
  locationLongitude?: number | null;
  locationRadiusMeters?: number | null;
};

const EARTH_RADIUS_METERS = 6371000;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function distanceMeters(
  latitudeA: number,
  longitudeA: number,
  latitudeB: number,
  longitudeB: number
): number {
  const deltaLat = toRadians(latitudeB - latitudeA);
  const deltaLon = toRadians(longitudeB - longitudeA);
  const latA = toRadians(latitudeA);
  const latB = toRadians(latitudeB);
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(latA) * Math.cos(latB) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function locationControlIsActive(store: StoreLocationControl | null | undefined): boolean {
  if (!store?.locationControlEnabled) return false;
  const radius = Number(store.locationRadiusMeters);
  const latitude = Number(store.locationLatitude);
  const longitude = Number(store.locationLongitude);
  return (
    Number.isFinite(radius) &&
    radius > 0 &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180
  );
}

async function positionFromPlugin(): Promise<{ latitude: number; longitude: number }> {
  const { checkPermissions, requestPermissions, getCurrentPosition } = await import(
    "@tauri-apps/plugin-geolocation"
  );
  let permissions = await checkPermissions();
  const granted =
    permissions.location === "granted" || permissions.coarseLocation === "granted";
  if (!granted) {
    permissions = await requestPermissions(["location"]);
  }
  const allowed =
    permissions.location === "granted" || permissions.coarseLocation === "granted";
  if (!allowed) {
    throw new Error("Location permission was not granted.");
  }
  const position = await getCurrentPosition({
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 0,
  });
  const latitude = position.coords.latitude;
  const longitude = position.coords.longitude;
  const accuracy = position.coords.accuracy;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || accuracy <= 0) {
    throw new Error("Desktop location plugin returned no reading.");
  }
  return { latitude, longitude };
}

function positionFromBrowser(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Location services are unavailable."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

export async function readCurrentPosition(): Promise<{ latitude: number; longitude: number }> {
  if (isTauriMode()) {
    try {
      return await positionFromPlugin();
    } catch (pluginError) {
      console.warn("[location] Desktop location plugin did not return a position.", pluginError);
    }
  }
  return positionFromBrowser();
}

export type LocationCheckResult =
  | { status: "allowed"; distanceMeters: number }
  | { status: "outside"; distanceMeters: number; radiusMeters: number }
  | { status: "unavailable" }
  | { status: "disabled" };

export async function checkStoreLocation(store: StoreLocationControl | null | undefined): Promise<LocationCheckResult> {
  if (!locationControlIsActive(store) || !store) return { status: "disabled" };
  try {
    const here = await readCurrentPosition();
    const distance = distanceMeters(
      Number(store.locationLatitude),
      Number(store.locationLongitude),
      here.latitude,
      here.longitude
    );
    const radius = Number(store.locationRadiusMeters);
    if (distance > radius) {
      return { status: "outside", distanceMeters: Math.round(distance), radiusMeters: radius };
    }
    return { status: "allowed", distanceMeters: Math.round(distance) };
  } catch (error) {
    console.warn("[location] Station location could not be verified.", error);
    return { status: "unavailable" };
  }
}
